"use client"

import { useEffect, useRef, useState } from "react"
import { usePathname } from "next/navigation"
import { MotionConfig, motion } from "motion/react"
import Link from "@/components/atoms/Link"
import {
  Dialog,
  DialogPanel,
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Popover,
  PopoverButton,
  PopoverGroup,
  PopoverPanel,
  useClose,
} from "@headlessui/react"
import ThemeToggle from "@/components/organisms/ThemeToggle"
import Logo from "@atoms/Logo"
import ContactDetails from "@molecules/ContactDetails"
import { Icon } from "@/shared/ui/icon"
import { products } from "@lib/products"

/** Простые пункты навигации (кроме выпадающего WEBDAD OS). */
const navLinks = [
  { href: "/store", label: "Store" },
  { href: "/projects", label: "Проекты" },
  { href: "/blog", label: "Блог" },
] as const

/** Единая подсветка «вы здесь» для всех активных пунктов меню: приподнятая
 *  плашка, как у сегментированного переключателя. Состояние передаётся
 *  объёмом, а не цветом: брендовый оранжевый остаётся за CTA. Плотный фон
 *  не смешивается с цветными hero (мятный и т.п.). */
const activeSurface =
  "bg-white shadow-[0_0_0_1px_rgb(15_23_42/0.05),0_1px_2px_rgb(15_23_42/0.08),0_6px_16px_-6px_rgb(15_23_42/0.22)] dark:bg-white/12 dark:shadow-[inset_0_1px_0_rgb(255_255_255/0.10),0_6px_16px_-6px_rgb(0_0_0/0.6)]"

/** Цвет текста активного пункта: полный контраст на фоне приглушённых соседей. */
const activeText = "text-gray-950 dark:text-white"

/** Пружина для подложек: быстро доезжает, без «резинки». */
const pillSpring = { type: "spring", stiffness: 420, damping: 34, mass: 0.8 } as const

/** Пункт верхней навигации. Активная страница — полный контраст текста
 *  и приподнятая подложка ActivePill; наведение — плоская HoverPill. */
const desktopNavLink = (active: boolean): string =>
  [
    "relative isolate inline-flex min-h-10 items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium transition-colors duration-200",
    active
      ? activeText
      : "text-gray-600 hover:text-gray-950 dark:text-white/60 dark:hover:text-white",
  ].join(" ")

/** Пункт мобильного меню: активная страница получает ту же подсветку,
 *  что и на десктопе. */
const mobileNavLink = (active: boolean): string =>
  [
    "-mx-3 flex min-h-11 items-center gap-2 rounded-lg px-3 py-2 text-base font-medium transition-colors",
    active
      ? `${activeSurface} ${activeText}`
      : "text-gray-900 hover:bg-gray-50 dark:text-white dark:hover:bg-white/5",
  ].join(" ")

/** Подложка активного пункта. Navbar живёт в layout и не размонтируется
 *  при переходах, поэтому по layoutId подложка переезжает со старого
 *  пункта на новый. */
function ActivePill() {
  return (
    <motion.span
      layoutId="nav-active-pill"
      transition={pillSpring}
      aria-hidden="true"
      className={`absolute inset-0 -z-10 rounded-full ${activeSurface}`}
    />
  )
}

/** Подложка наведения: скользит за курсором между неактивными пунктами. */
function HoverPill() {
  return (
    <motion.span
      layoutId="nav-hover-pill"
      transition={pillSpring}
      aria-hidden="true"
      className="absolute inset-0 -z-10 rounded-full bg-gray-900/5 dark:bg-white/10"
    />
  )
}

/** Закрывает содержащий Popover по клику/тапу вне переданного контейнера.
 *  Явное дублирование встроенного outside-click Headless UI — страхует
 *  случаи, когда стандартная детекция не срабатывает. Рендерится внутри
 *  <Popover>, поэтому useClose() имеет доступ к его контексту. */
function OutsideCloser({
  containerRef,
}: {
  containerRef: React.RefObject<HTMLElement | null>
}) {
  const close = useClose()

  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      const container = containerRef.current
      if (container && !container.contains(event.target as Node)) {
        close()
      }
    }
    document.addEventListener("pointerdown", handlePointerDown)
    return () => document.removeEventListener("pointerdown", handlePointerDown)
  }, [close, containerRef])

  return null
}

export default function Navbar() {
  const pathname = usePathname()
  const productsMenuRef = useRef<HTMLDivElement>(null)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  /** href пункта под курсором — по нему рисуется HoverPill. */
  const [hovered, setHovered] = useState<string | null>(null)

  /** Активна ли ссылка: точное совпадение либо вложенный маршрут. */
  const isActive = (href: string): boolean =>
    pathname === href || pathname.startsWith(`${href}/`)
  const productsActive = isActive("/os")

  useEffect(() => {
    let ticking = false

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 0)
          ticking = false
        })
        ticking = true
      }
    }

    handleScroll()
    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  return (
    <header className="sticky top-0 inset-x-0 z-50 pt-2 px-2">
      <nav
        aria-label="Global"
        className={`mx-auto flex max-w-7xl items-center justify-between px-6 py-1 rounded-full transition-all duration-300 ${
          isScrolled
            ? "bg-white/70 backdrop-blur-lg shadow-xl border border-gray-900/10 dark:border-white/20 dark:bg-gray-900/90 dark:shadow-2xl"
            : "bg-transparent border border-transparent"
        }`}
      >
        <div className="flex lg:flex-1">
          <Link href="/" className="p-1.5" aria-label="На главную">
            <span className="sr-only">На главную</span>
            <Logo alt="WEBDAD" />
          </Link>
        </div>
        <div className="flex items-center gap-2 lg:hidden">
          <ThemeToggle />
          <button
            type="button"
            onClick={() => setMobileMenuOpen(true)}
            className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-700 dark:text-gray-400"
          >
            <span className="sr-only">Открыть меню</span>
            <Icon name="hamburger-menu-linear" className="size-6" />
          </button>
        </div>

        <MotionConfig reducedMotion="user">
          <PopoverGroup
            className="hidden lg:flex lg:items-center lg:gap-x-1"
            onMouseLeave={() => setHovered(null)}
          >
            <div ref={productsMenuRef} className="relative">
              <Popover>
                {({ open }) => (
                  <>
                    <OutsideCloser containerRef={productsMenuRef} />
                    <PopoverButton
                      onMouseEnter={() => setHovered("/os")}
                      className={`group cursor-pointer ${desktopNavLink(productsActive)} ${
                        productsActive
                          ? ""
                          : "data-open:text-gray-950 dark:data-open:text-white"
                      }`}
                    >
                      {productsActive && <ActivePill />}
                      {!productsActive &&
                        (hovered === "/os" || (open && hovered === null)) && (
                          <HoverPill />
                        )}
                      WEBDAD OS
                      <Icon
                        name="alt-arrow-down-linear"
                        className="size-5 flex-none transition-transform group-data-open:rotate-180"
                        aria-hidden="true"
                      />
                    </PopoverButton>

                    <PopoverPanel
                      transition
                      className="absolute left-1/2 z-10 mt-3 w-screen max-w-4xl -translate-x-1/2 overflow-hidden rounded-3xl bg-white shadow-lg outline-1 outline-gray-900/5 transition data-closed:translate-y-1 data-closed:opacity-0 data-enter:duration-200 data-enter:ease-out data-leave:duration-150 data-leave:ease-in dark:bg-gray-800 dark:shadow-none dark:-outline-offset-1 dark:outline-white/10"
                    >
                      <ul className="grid grid-cols-3 gap-x-4 p-6">
                        {products.map((item) => {
                          const active = isActive(item.href)
                          return (
                            <li key={item.slug}>
                              <a
                                href={item.href}
                                aria-current={active ? "page" : undefined}
                                className={`group/item flex h-full items-start gap-x-3 rounded-xl p-3 transition-colors ${
                                  active
                                    ? activeSurface
                                    : "hover:bg-gray-50 dark:hover:bg-white/5"
                                }`}
                              >
                                <Icon
                                  name={item.icon}
                                  className={`size-6 flex-none transition-colors ${
                                    active
                                      ? "text-brand-600 dark:text-jungle-300"
                                      : "text-gray-500 group-hover/item:text-brand-600 dark:text-gray-400 dark:group-hover/item:text-jungle-300"
                                  }`}
                                />
                                <span className="min-w-0">
                                  <span
                                    className={`block text-sm font-semibold ${
                                      active
                                        ? activeText
                                        : "text-gray-900 dark:text-white"
                                    }`}
                                  >
                                    {item.name}
                                  </span>
                                  <span className="mt-0.5 block text-xs text-gray-500 dark:text-white/50">
                                    {item.tagline}
                                  </span>
                                </span>
                              </a>
                            </li>
                          )
                        })}
                      </ul>

                      <div className="flex items-center justify-end gap-4 border-t border-gray-900/5 bg-gray-50 px-6 py-3 dark:border-white/10 dark:bg-white/5">
                        <a
                          href="/os"
                          aria-current={pathname === "/os" ? "page" : undefined}
                          className="inline-flex flex-none items-center gap-2 text-sm font-medium text-gray-900 hover:text-brand-600 dark:text-white dark:hover:text-jungle-300"
                        >
                          Все решения
                          <Icon name="arrow-right-linear" className="size-4" />
                        </a>
                      </div>
                    </PopoverPanel>
                  </>
                )}
              </Popover>
            </div>
            {navLinks.map(({ href, label }) => {
              const active = isActive(href)
              return (
                <Link
                  key={href}
                  href={href}
                  onMouseEnter={() => setHovered(href)}
                  aria-current={active ? "page" : undefined}
                  className={desktopNavLink(active)}
                >
                  {active && <ActivePill />}
                  {!active && hovered === href && <HoverPill />}
                  {label}
                </Link>
              )
            })}
          </PopoverGroup>
        </MotionConfig>
        <div className="hidden lg:flex lg:flex-1 lg:justify-end items-center gap-3">
          <ThemeToggle />
          <a
            href="https://t.me/webdadby"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Написать в Telegram"
            className="inline-flex items-center h-11 -mr-5 gap-3 rounded-full bg-linear-to-r from-brand-500 to-brand-600 px-1 py-1 text-sm font-medium text-white shadow-sm active:scale-100 transition-all duration-200"
          >
            <span className="pl-4">Написать</span>
            <span className="flex items-center justify-center rounded-full bg-white h-full w-9">
              <Icon name="plain-bold-duotone" size={20} className="text-brand-600" />
            </span>
          </a>
        </div>
      </nav>
      <Dialog open={mobileMenuOpen} onClose={setMobileMenuOpen} className="lg:hidden">
        <div className="fixed inset-0 z-50" />
        <DialogPanel className="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-white p-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10 dark:bg-gray-900 dark:sm:ring-gray-100/10">
          <div className="flex items-center justify-between">
            <Link href="/" className="-m-1.5 p-1.5" aria-label="На главную">
              <span className="sr-only">На главную</span>
              <Logo showLabel={false} alt="WEBDAD" />
            </Link>
            <div className="flex items-center gap-2">
              <ThemeToggle />
              <button
                type="button"
                onClick={() => setMobileMenuOpen(false)}
                className="-m-2.5 rounded-md p-2.5 text-gray-700 dark:text-gray-400"
              >
                <span className="sr-only">Закрыть меню</span>
                <Icon name="hamburger-menu-linear" className="size-6" />
              </button>
            </div>
          </div>
          <div className="mt-6 flow-root">
            <div className="-my-6 divide-y divide-gray-500/10 dark:divide-white/10">
              <div className="space-y-2 py-6">
                {/* На странице продукта раздел раскрыт сразу, чтобы активный
                    пункт был виден без лишнего тапа. */}
                <Disclosure as="div" className="-mx-3" defaultOpen={productsActive}>
                  <DisclosureButton
                    className="group flex min-h-11 w-full items-center justify-between rounded-lg py-2 pr-3.5 pl-3 text-base font-medium text-gray-900 hover:bg-gray-50 dark:text-white dark:hover:bg-white/5"
                    aria-label="WEBDAD OS"
                  >
                    WEBDAD OS
                    <Icon
                      name="alt-arrow-down-linear"
                      size={20}
                      className="size-5 flex-none group-data-open:rotate-180"
                    />
                  </DisclosureButton>
                  <DisclosurePanel className="mt-2 space-y-4">
                    <div className="space-y-1">
                      {products.map((item) => {
                        const active = isActive(item.href)
                        return (
                          <DisclosureButton
                            key={item.slug}
                            as="a"
                            href={item.href}
                            onClick={() => setMobileMenuOpen(false)}
                            aria-current={active ? "page" : undefined}
                            className={`flex min-h-11 items-center gap-3 rounded-lg py-2 pr-3 pl-6 text-sm font-medium transition-colors ${
                              active
                                ? `${activeSurface} ${activeText}`
                                : "text-gray-900 hover:bg-gray-50 dark:text-white/80 dark:hover:bg-white/5"
                            }`}
                          >
                            <Icon
                              name={item.icon}
                              className={`size-5 flex-none ${
                                active
                                  ? "text-brand-600 dark:text-jungle-300"
                                  : "text-gray-400 dark:text-white/40"
                              }`}
                            />
                            {item.name}
                          </DisclosureButton>
                        )
                      })}
                    </div>

                    <DisclosureButton
                      as="a"
                      href="/os"
                      onClick={() => setMobileMenuOpen(false)}
                      aria-current={pathname === "/os" ? "page" : undefined}
                      className="flex min-h-11 items-center gap-2 rounded-lg py-2 pr-3 pl-6 text-sm font-medium text-brand-700 hover:bg-gray-50 dark:text-jungle-300 dark:hover:bg-white/5"
                    >
                      Все решения
                      <Icon name="arrow-right-linear" className="size-4" />
                    </DisclosureButton>
                  </DisclosurePanel>
                </Disclosure>
                {navLinks.map(({ href, label }) => (
                  <Link
                    key={href}
                    href={href}
                    onClick={() => setMobileMenuOpen(false)}
                    aria-current={isActive(href) ? "page" : undefined}
                    className={mobileNavLink(isActive(href))}
                  >
                    {label}
                  </Link>
                ))}
              </div>
              <div className="py-6 space-y-4">
                <ContactDetails
                  variant="default"
                  layout="stack"
                  bordered={false}
                  className="-ml-3"
                />
              </div>
            </div>
          </div>
        </DialogPanel>
      </Dialog>
    </header>
  )
}
